import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function Messaging() {
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tpl, setTpl] = useState({ name: "", body: "" });
  const [msg, setMsg] = useState({ to: "", body: "" });

  const load = async () => {
    const [t, m] = await Promise.all([api.get("/messages/templates"), api.get("/messages")]);
    setTemplates(t.data); setLogs(m.data);
  };
  useEffect(() => { load(); }, []);

  const addTemplate = async () => {
    try { await api.post("/messages/templates", tpl); toast.success("Template saved"); setTpl({ name: "", body: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const send = async () => {
    try {
      const to = msg.to.split(",").map(x => x.trim()).filter(Boolean);
      await api.post("/messages/send", { to, body: msg.body });
      toast.success("Message queued (MOCKED WHATSAPP)");
      setMsg({ to: "", body: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <DashLayout variant="school" title="WhatsApp Messaging">
      <div className="mb-4 flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
        <MessageSquare className="w-4 h-4" /> <b>MOCKED WHATSAPP</b> — messages are stored in the database. Enable real Twilio WhatsApp API in production.
      </div>
      <Tabs defaultValue="send">
        <TabsList>
          <TabsTrigger value="send" data-testid="tab-msg-send">Send Message</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-msg-templates">Templates</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-msg-logs">Sent Log</TabsTrigger>
        </TabsList>

        <TabsContent value="send">
          <Card className="p-6 max-w-2xl space-y-4">
            <div><label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Recipients (comma-separated phones/names)</label>
              <Input value={msg.to} onChange={(e) => setMsg({ ...msg, to: e.target.value })} data-testid="msg-to" /></div>
            <div><label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Message</label>
              <Textarea rows={4} value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })} data-testid="msg-body" /></div>
            <div className="flex gap-2 flex-wrap">
              {templates.map((t) => <Button key={t.id} type="button" size="sm" variant="outline" onClick={() => setMsg({ ...msg, body: t.body })} data-testid={`use-tpl-${t.id}`}>Use: {t.name}</Button>)}
            </div>
            <Button onClick={send} className="bg-emerald-600 hover:bg-emerald-700" data-testid="msg-send"><Send className="w-4 h-4 mr-2" />Send WhatsApp</Button>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card className="p-4 flex flex-wrap gap-3 items-end mb-4">
            <Input placeholder="Template Name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} data-testid="tpl-name" />
            <Input placeholder="Body (supports {student_name}, {class_name})" value={tpl.body} onChange={(e) => setTpl({ ...tpl, body: e.target.value })} className="flex-1 min-w-64" data-testid="tpl-body" />
            <Button onClick={addTemplate} className="bg-blue-700 hover:bg-blue-800" data-testid="tpl-add"><Plus className="w-4 h-4 mr-1" />Add</Button>
          </Card>
          <div className="bg-white border rounded-lg">
            <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Body</TableHead></TableRow></TableHeader>
              <TableBody>{templates.map(t => <TableRow key={t.id}><TableCell className="font-semibold">{t.name}</TableCell><TableCell className="text-slate-600">{t.body}</TableCell></TableRow>)}</TableBody></Table>
          </div>
        </TabsContent>

        <TabsContent value="logs">
          <div className="bg-white border rounded-lg">
            <Table><TableHeader><TableRow><TableHead>To</TableHead><TableHead>Body</TableHead><TableHead>Status</TableHead><TableHead>Sent</TableHead></TableRow></TableHeader>
              <TableBody>{logs.map(l => <TableRow key={l.id} data-testid={`row-msg-${l.id}`}><TableCell className="text-xs">{(l.to || []).join(", ")}</TableCell><TableCell className="max-w-md truncate">{l.body}</TableCell><TableCell><Badge className="bg-amber-500">{l.status}</Badge></TableCell><TableCell className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</TableCell></TableRow>)}</TableBody></Table>
          </div>
        </TabsContent>
      </Tabs>
    </DashLayout>
  );
}
